/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Log } from '../logging/Log';

export class DimensionScaler {
    private static instance: DimensionScaler;

    private static readonly TAG = 'DimensionScaler';

    private widthRatio: number;

    private heightRatio: number;

    private readonly BASE_WIDTH = 1920;

    private readonly BASE_HEIGHT = 1080;

    private constructor(currentWidth: number, currentHeight: number) {
        this.widthRatio = currentWidth / this.BASE_WIDTH;
        this.heightRatio = currentHeight / this.BASE_HEIGHT;
    }

    /**
     * Initialize the singleton ONCE with screen dimensions.
     * Should be called only at app startup.
     */
    public static initialize(currentWidth: number, currentHeight: number): void {
        if (!DimensionScaler.instance) {
            Log.i(
                DimensionScaler.TAG,
                `Initializing DimensionScaler with width: ${currentWidth}, height: ${currentHeight}`,
            );
            if (currentWidth <= 0 || currentHeight <= 0) {
                Log.e(
                    DimensionScaler.TAG,
                    'Invalid dimensions provided for DimensionScaler initialization.',
                );
                throw new Error('Invalid dimensions provided for DimensionScaler initialization.');
            }
            DimensionScaler.instance = new DimensionScaler(currentWidth, currentHeight);
        } else {
            Log.w(
                DimensionScaler.TAG,
                'DimensionScaler is already initialized. Initialization ignored.',
            );
        }
    }

    /**
     * Use this to access the singleton instance after initialization.
     * Throws error if accessed before initialize().
     */
    public static getInstance(): DimensionScaler {
        if (!DimensionScaler.instance) {
            Log.e(
                DimensionScaler.TAG,
                'DimensionScaler.getInstance() called before initialization.',
            );
            throw new Error('DimensionScaler is not initialized. Call initialize() first.');
        }
        return DimensionScaler.instance;
    }

    public getScaledDimensions(
        originalWidth: number,
        originalHeight: number,
        roundOff: boolean = true,
    ): { width: number; height: number } {
        const scaledWidth = originalWidth * (this.widthRatio || 1);
        const scaledHeight = originalHeight * (this.heightRatio || 1);
        return {
            width: roundOff ? Math.round(scaledWidth) : scaledWidth,
            height: roundOff ? Math.round(scaledHeight) : scaledHeight,
        };
    }

    public getScaledWidth(originalWidth: number, roundOff: boolean = true): number {
        const scaledWidth = originalWidth * (this.widthRatio || 1);
        return roundOff ? Math.round(scaledWidth) : scaledWidth;
    }

    public getScaledHeight(originalHeight: number, roundOff: boolean = true): number {
        const scaledHeight = originalHeight * (this.heightRatio || 1);
        return roundOff ? Math.round(scaledHeight) : scaledHeight;
    }
}
